import styled, { css } from 'styled-components';
import { CHIP_ICON_POSITION, CHIP_SIZE, CHIP_TYPE, EMPTY_STATE_SIZE } from '../../global/constants';
import { Typography } from '../../global/theme';
import { mobile_xl } from '../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, FlexCenter } from '../../global/commonStyle';

/**
 * Props interface for the ChipWrapper styled component
 * @param {number} variant - The visual variant/style type of the chip
 * @param {number} size - The size of the chip (small/medium)
 * @param {boolean} [isDisabled] - Optional boolean to determine if the chip is disabled
 * @param {boolean} [isActive] - Optional boolean to determine if the chip is active
 * @param {number} [iconPosition] - Optional position configuration for icons
 */
interface Props {
  variant: number;
  size: number;
  isDisabled?: boolean;
  isActive?: boolean;
  iconPosition?: number;
  disableHoverEffect?: boolean;
  isInputField?: boolean;
  isAvatar?: boolean;
  isMulticolorIcon?: boolean;
}

interface ChipProps {
  textColorType?: 'primary' | 'secondary';
  isLeadingIcon?: boolean;
}

export const ChipText = styled.p<ChipProps>`
  ${Typography.body_md_medium}
  color: ${({ textColorType }) => (textColorType === 'primary' ? 'var(--text-primary)' : 'var(--text-secondary)')};
  margin: var(--space-0);
`;

export const ChipIcon = styled.div<ChipProps>`
  ${FlexCenter}
  padding-block: ${({ isLeadingIcon }) => (isLeadingIcon ? 'var(--space-2)' : 'var(--space-1)')};
  ${mobile_xl(css<ChipProps>`
    padding-block: ${({ isLeadingIcon }) => (isLeadingIcon ? 'var(--space-3)' : 'var(--space-2)')};
  `)}
`;

export const ChipWrapper = styled.div<Props>`
  ${FlexBetween}
  gap: ${({ size }) => (size === CHIP_SIZE.MEDIUM ? 'var(--space-10)' : 'var(--space-4)')};
  padding: ${({ size }) =>
    size === CHIP_SIZE.MEDIUM ? 'var(--space-6) var(--space-12)' : 'var(--space-3) var(--space-8)'};
  border-radius: ${({ isInputField, size }) =>
    isInputField && size === EMPTY_STATE_SIZE.SMALL ? 'var(--border-radius-4)' : 'var(--border-radius-8)'};
  box-shadow: ${({ variant }) => (variant === CHIP_TYPE.FILL ? 'var(--border)' : 'none')};
  background-color: ${({ variant }) => (variant === CHIP_TYPE.FILL ? 'var(--background-primary)' : 'transparent')};
  cursor: pointer;

  &:hover {
    background-color: ${({ variant }) =>
      variant === CHIP_TYPE.EMPTY ? 'var(--background-primary)' : 'var(--neutral-secondary)'};
    box-shadow: ${({ variant }) => (variant === CHIP_TYPE.FILL_WITHOUT_BORDER ? 'none' : 'var(--border)')};
    ${ChipText} {
      color: var(--text-primary);
    }
    ${ChipIcon} {
      svg path {
        fill: ${({ isMulticolorIcon }) => (isMulticolorIcon ? undefined : 'var(--text-primary)')};
      }
    }
    .icon-button:hover {
      background-color: ${({ variant }) => (variant === CHIP_TYPE.FILL ? 'var(--background-muted)' : '')};
    }
  }

  &.profile-tag {
    background-color: transparent;
  }
  .profile-icon-button:hover {
    background-color: var(--neutral-secondary);
  }

  ${({ isAvatar, size }) =>
    isAvatar &&
    size === EMPTY_STATE_SIZE.MEDIUM &&
    css`
      padding-block: var(--space-4);
    `}

  ${({ disableHoverEffect, variant }) =>
    disableHoverEffect &&
    css`
      &:hover {
        background-color: ${variant === CHIP_TYPE.FILL ? 'var(--background-primary)' : 'transparent'};
        box-shadow: ${variant === CHIP_TYPE.FILL ? 'var(--border)' : 'none'};
        cursor: default;
      }
    `}

  /* icon Position */
  ${({ iconPosition, size }) =>
    iconPosition === CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON &&
    css`
      padding-inline: ${size === CHIP_SIZE.MEDIUM ? 'var(--space-12) var(--space-8)' : 'var(--space-8) var(--space-4)'};
    `}
  ${({ iconPosition, size }) =>
    iconPosition === CHIP_ICON_POSITION.AVATAR_TEXT &&
    css`
      padding-inline: ${size === CHIP_SIZE.MEDIUM ? 'var(--space-8) var(--space-12)' : 'var(--space-4) var(--space-8)'};
    `}
  ${({ iconPosition, size }) =>
    iconPosition === CHIP_ICON_POSITION.AVATAR_TEXT_RIGHT_BUTTON &&
    css`
      padding-inline: ${size === CHIP_SIZE.MEDIUM ? 'var(--space-8)' : 'var(--space-4)'};
    `}    
    /* disabled state */
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background-color: transparent;
      ${ChipText} {
        color: var(--text-muted);
      }
      ${ChipIcon} {
        svg path {
          fill: var(--text-muted);
        }
      }
      cursor: not-allowed;
      pointer-events: none;
      &:hover {
        background-color: unset;
        ${ChipText} {
          color: var(--text-muted);
        }
        svg path {
          fill: var(--text-muted);
        }
      }
    `}

    /* active state */
${({ isActive, variant, isMulticolorIcon }) =>
    isActive &&
    css`
      background-color: ${variant === CHIP_TYPE.EMPTY ? 'var(--background-primary)' : 'var(--neutral-secondary)'};
      box-shadow: ${variant === CHIP_TYPE.FILL_WITHOUT_BORDER ? 'none' : 'var(--border)'};
      ${ChipText} {
        color: var(--text-primary);
      }
      ${ChipIcon} {
        svg path {
          fill: ${isMulticolorIcon ? undefined : 'var(--text-primary)'};
        }
      }
      &:hover {
        background-color: ${variant === CHIP_TYPE.EMPTY ? 'var(--background-primary)' : 'var(--neutral-secondary)'};
      }
    `}

  /* icon button hover */
  button:hover {
    background-color: unset;
  }
`;

export const ChipLeftSection = styled.div<{ size: number }>`
  ${AlignCenter}
  gap: ${({ size }) => (size === CHIP_SIZE.MEDIUM ? 'var(--space-10)' : 'var(--space-6)')};
`;
